import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Container, Card, Typography, Button } from "@mui/material";

interface BaseValue {
  id: number;
  contador: number;
}

const baseValues: BaseValue[] = [
  { id: 1, contador: 100000 },
  { id: 2, contador: 150 },
  { id: 3, contador: 60 },
  { id: 4, contador: 50 },
  { id: 5, contador: 80 },
  { id: 6, contador: 5 },
];

export default function ContadoresDestacados() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contadores, setContadores] = useState<BaseValue[]>(
    baseValues.map((c) => ({
      ...c,
      contador: 0,
    })),
  );
  const contadoresRef = useRef<HTMLDivElement>(null);
  const animandoRef = useRef(false);

  function animarContadores() {
    animandoRef.current = true;
    const duracionAnimacion = 2000;
    const inicio = performance.now();

    function animate(time: number) {
      const progreso = Math.min((time - inicio) / duracionAnimacion, 1);
      const nuevosValores = baseValues.map((caja) => ({
        ...caja,
        contador: Math.floor(caja.contador * progreso),
      }));
      setContadores(nuevosValores);

      if (progreso < 1) {
        requestAnimationFrame(animate);
      } else {
        animandoRef.current = false;
      }
    }

    requestAnimationFrame(animate);
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animandoRef.current) {
          setContadores(
            baseValues.map((c) => ({
              ...c,
              contador: 0,
            })),
          );
          animarContadores();
        }
      },
      { threshold: 0.2 },
    );

    const current = contadoresRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <Box
      ref={contadoresRef}
      component="section"
      sx={{
        background: "#0f172a", // slate-900
        borderTop: "1px solid #1e293b", // slate-800
        borderBottom: "1px solid #1e293b",
        py: { xs: 12, md: 14 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          right: "-10%",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          backgroundColor: "rgba(0, 162, 255, 0.05)",
          filter: "blur(120px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-10%",
          left: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          backgroundColor: "rgba(0, 162, 255, 0.05)",
          filter: "blur(100px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 10, px: { xs: 3, md: 4 } }}>
        <Box sx={{ mb: { xs: 8, md: 10 }, textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.875rem", md: "2.25rem" }, // 3xl to 4xl
              color: "#ffffff",
              mb: 2,
              letterSpacing: "-0.025em"
            }}
          >
            {t("contadores.title")}
          </Typography>
          <Box
            sx={{
              width: "80px", // w-20
              height: "4px", // h-1
              bgcolor: "#0062ff", // primary-500 approx
              borderRadius: "9999px",
              mx: "auto",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: { xs: 2, md: 4 }, // gap-6 md:gap-8
            alignItems: "stretch",
          }}
        >
          {contadores.map((caja) => (
            <Box
              key={caja.id}
              className="group"
              sx={{
                p: { xs: 2.5, sm: 4, md: 4 }, // p-8
                borderRadius: "16px", // rounded-2xl
                border: "1px solid #1e293b", // slate-800
                bgcolor: "rgba(30, 41, 59, 0.5)", // slate-800/50
                backdropFilter: "blur(4px)",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // shadow-xl
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "#1e293b", // slate-800
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: "#38bdf8", // primary-400 (light blue)
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" }, // 4xl to 5xl
                  mb: 1.5,
                  display: "block",
                  transition: "color 0.3s ease",
                  position: "relative",
                  zIndex: 10,
                  ".group:hover &": {
                    color: "#7dd3fc", // primary-300
                  }
                }}
              >
                {caja.contador.toLocaleString("es-AR")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#cbd5e1", // slate-300
                  fontSize: "0.875rem", // text-sm
                  lineHeight: 1.625,
                  fontWeight: 500,
                  transition: "color 0.3s ease",
                  position: "relative",
                  zIndex: 10,
                  ".group:hover &": {
                    color: "#ffffff",
                  }
                }}
              >
                {t(`contadores.items.${caja.id - 1}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
